import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from '../common/Alert'
import tripContractService from '../../services/tripContractService'
import AddPaymentModal from './AddPaymentModal'
import BatchPaymentModal from './BatchPaymentModal'
import { ArrowLeft, Edit3, Download, Plus, Zap } from 'lucide-react'
import Button from '../common/Button.js'import { useTranslation } from 'react-i18next';


const ViewTripContract = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { tripId } = useParams()
  const [loading, setLoading] = useState(true)
  const [trip, setTrip] = useState(null)
  const [payments, setPayments] = useState([])
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false)
  const [showBatchPaymentModal, setShowBatchPaymentModal] = useState(false)
  const [downloadingInvoice, setDownloadingInvoice] = useState(false)

  useEffect(() => {
    fetchTripDetails()
  }, [tripId])

  const fetchTripDetails = async () => {
    try {
      setLoading(true)
      const response = await tripContractService.getOne(tripId)
      const tripData = response.data.data
      setTrip(tripData)
      setPayments(tripData.payments || [])
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load trip contract')
      navigate('/trip-contracts')
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadInvoice = async () => {
    try {
      setDownloadingInvoice(true)
      const response = await tripContractService.downloadInvoice(tripId)
      
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `invoice_${trip.invoice_no}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      Alert.success('Invoice downloaded successfully!')
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to download invoice')
      console.error(err)
    } finally {
      setDownloadingInvoice(false)
    }
  }

  const closeAddPaymentModal = () => {
    setShowAddPaymentModal(false)
    fetchTripDetails()
  }

  const closeBatchPaymentModal = () => {
    setShowBatchPaymentModal(false)
    fetchTripDetails()
  }

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : ''
  }

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      'paid': 'bg-green-100 text-green-700',
      'pending': 'bg-yellow-100 text-yellow-700',
      'active': 'bg-blue-100 text-blue-700',
      'cancelled': 'bg-red-100 text-red-700'
    }
    return colors[status] || 'bg-gray-100 text-gray-700'
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading trip contract..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!trip) {
    return null
  }

  const balance = (trip.total_cost || 0) - (trip.paid_amount || 0)

  return (
    <DashboardLayout>
      <div className="max-w-4xl">
        {/* Page Header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/trip-contracts')} icon={ArrowLeft} />
            <div>
              <h1 className="text-2xl font-bold text-secondary">Trip Contract Details</h1>
              <p className="text-sm text-gray-600 mt-1">Trip ID: {trip.trip_id}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/trip-contracts/edit/${tripId}`)}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition"
            >
              <Edit3 className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={handleDownloadInvoice}
              disabled={downloadingInvoice}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              Invoice
            </button>
          </div>
        </div>

        {/* Contract Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contract Information</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Trip ID:</span>
                <span className="font-medium text-gray-900">{trip.trip_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Invoice No:</span>
                <span className="font-medium text-gray-900">{trip.invoice_no}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Party:</span>
                <span className="font-medium text-gray-900">{trip.contract_party_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Vehicle:</span>
                <span className="font-medium text-gray-900">{trip.vehicle_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Delivery Location:</span>
                <span className="font-medium text-gray-900">{trip.delivery_location}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(trip.status)}`}>
                  {capitalizeStatus(trip.status)}
                </span>
              </div>
            </div>
          </div>

          {/* Cost Summary */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Base Cost:</span>
                <span className="font-medium text-gray-900">{formatCurrency(trip.base_trip_cost)}</span>
              </div>
              {trip.handling > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Handling:</span>
                  <span className="font-medium text-gray-900">{formatCurrency(trip.handling)}</span>
                </div>
              )}
              {trip.parking > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Parking:</span>
                  <span className="font-medium text-gray-900">{formatCurrency(trip.parking)}</span>
                </div>
              )}
              {trip.other > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Other:</span>
                  <span className="font-medium text-gray-900">{formatCurrency(trip.other)}</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-3 flex justify-between font-semibold">
                <span>Total Cost:</span>
                <span className="text-orange-600">{formatCurrency(trip.total_cost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Paid Amount:</span>
                <span className="font-medium text-green-600">{formatCurrency(trip.paid_amount)}</span>
              </div>
              <div className={`flex justify-between font-semibold py-2 px-3 rounded-lg ${balance > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                <span>Balance:</span>
                <span className={balance > 0 ? 'text-yellow-600' : 'text-green-600'}>
                  {formatCurrency(balance)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Actions */}
        {balance > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Options</h3>
            <div className="flex flex-col sm:flex-row gap-4">
                      <Button variant="success" onClick={() => setShowAddPaymentModal(true)} icon={Plus} size="lg">
                        Add Single Payment
                      </Button>
                      <Button variant="primary" onClick={() => setShowBatchPaymentModal(true)} icon={Zap} size="lg">
                        Batch Payment
                      </Button>
            </div>
          </div>
        )}

        {/* Payment History */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
          </div>
          {payments.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No payments recorded yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Payment ID</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Mode</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Reference</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{payment.id}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-green-600">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(payment.payment_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{payment.payment_mode}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{payment.reference_number || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{payment.remarks || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAddPaymentModal && (
        <AddPaymentModal
          isOpen={showAddPaymentModal}
          tripId={tripId}
          balance={balance}
          onClose={closeAddPaymentModal}
        />
      )}

      {showBatchPaymentModal && (
        <BatchPaymentModal
          isOpen={showBatchPaymentModal}
          partyId={trip.contract_party_id}
          partyName={trip.contract_party_name}
          onClose={closeBatchPaymentModal}
        />
      )}
    </DashboardLayout>
  )
}

export default ViewTripContract
